import React, { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";
import { useMusicPlayer } from "@/lib/MusicPlayerContext";

/**
 * Bottom-nav "Music" tab — pure UI. All audio state (element,
 * playlist, index, prefs, volume, muted) lives in
 * `MusicPlayerProvider` at the App root so playback survives route
 * changes. This component only surfaces the mute toggle + volume
 * slider.
 *
 * Hides entirely when the playlist is empty OR the super-admin has
 * disabled the `music` platform feature.
 */
export default function MusicButton({ active }) {
  const { hasTracks, musicKilled, muted, volume, toggleMute, setVolume } = useMusicPlayer();
  const [open, setOpen] = useState(false);
  const popRef = useRef(null);

  // Close popover on outside click.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener("mousedown", onDown);
    window.addEventListener("touchstart", onDown, { passive: true });
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("touchstart", onDown);
    };
  }, [open]);

  if (musicKilled || !hasTracks) return null;

  const Icon = muted ? VolumeX : Volume2;

  return (
    <div className="relative">
      {open && (
        <div
          ref={popRef}
          data-testid="music-popover"
          className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-white/98 backdrop-blur border border-stone-200 shadow-lg rounded-2xl p-3 w-56"
        >
          <div className="flex items-center gap-2">
            <button
              onClick={toggleMute}
              data-testid="music-mute"
              aria-label={muted ? "Turn music on" : "Mute music"}
              className="w-9 h-9 rounded-full grid place-items-center text-white shrink-0"
              style={{ backgroundColor: "var(--tenant-primary)" }}
            >
              {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>
            <input
              type="range"
              min={0}
              max={1}
              step={0.01}
              value={volume}
              onChange={(e) => setVolume(parseFloat(e.target.value))}
              className="flex-1 accent-[color:var(--tenant-primary)]"
              data-testid="music-volume"
              aria-label="Volume"
            />
          </div>
          <div className="text-[10px] text-stone-500 mt-2 leading-relaxed">
            {muted ? "Tap the speaker to play." : "Streaming church playlist."}
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="bottomnav-music"
        aria-label="Music"
        className="flex flex-col items-center gap-1 py-1 px-3 min-w-[56px]"
      >
        <Icon
          className="w-5 h-5"
          style={{ color: active || open ? "var(--tenant-primary)" : "#78716C" }}
          strokeWidth={active || open ? 2.2 : 1.6}
        />
        <span
          className="text-[10px] font-medium tracking-wide"
          style={{ color: active || open ? "var(--tenant-primary)" : "#78716C" }}
        >
          Music
        </span>
      </button>
    </div>
  );
}
